import { Node } from "@xyflow/react";
import { FC, Suspense } from "react";
import { useAtomValue } from "jotai";
import {
  nodeAtomFamily,
  tableFieldsAtomFamily_SUSPENDS,
  DefaultNodeData,
  suggestedNodeAtomFamily,
  extractNodeIdFromSuggestedNodeId,
} from "@zapier/canvas-state";

import styles from "./TablePreview.module.css";

interface TableField {
  name: string;
  type: string;
}

interface TablePreviewProps {
  nodeId: string;
}

// Sample test data
const testRows = [
  { id: 1, name: "John Doe", email: "john@example.com", role: "Admin" },
  { id: 2, name: "Jane Smith", email: "jane@example.com", role: "User" },
  { id: 3, name: "Bob Johnson", email: "bob@example.com", role: "Editor" },
];

function SuspendedComponent({ nodeId }: TablePreviewProps) {
  const node = useAtomValue(nodeAtomFamily(nodeId)) as Node<DefaultNodeData>;
  const suggestedNode = useAtomValue(suggestedNodeAtomFamily(nodeId));
  const nodeOrSuggestedNode = node ?? suggestedNode;

  const tableId = nodeOrSuggestedNode.data.tableId!;
  const tableFields = useAtomValue(
    tableFieldsAtomFamily_SUSPENDS({
      nodeId: extractNodeIdFromSuggestedNodeId(nodeId),
      tableId,
      proposalId: nodeOrSuggestedNode.data.proposalId,
    }),
  );

  return (
    <div className={styles.tablePreview}>
      <div className={styles.header}>
        <div className={styles.headerCell}>
          <span>#</span>
        </div>
        {tableFields.map((field: TableField) => (
          <div key={field.name} className={styles.headerCell}>
            <span>{field.name}</span>
          </div>
        ))}
      </div>
      <div className={styles.body}>
        {testRows.map((row, index) => (
          <div key={row.id} className={styles.row}>
            <div className={styles.cell}>
              <span>{index + 1}</span>
            </div>
            {tableFields.map((field: TableField) => (
              <div key={`${row.id}-${field.name}`} className={styles.cell}>
                <span>{row[field.name as keyof typeof row] || ""}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export const TablePreview: FC<TablePreviewProps> = ({ nodeId }) => {
  return (
    <Suspense fallback={null}>
      <SuspendedComponent nodeId={nodeId} />
    </Suspense>
  );
};
